import { useSession } from '../contexts';
import { useEffect, useState, useCallback } from 'react';
import { getSessionStatus, type SessionStatus } from '../services/auth';

export default function DashboardPage() {
  const { accountAddress } = useSession();
  const [serverSession, setServerSession] = useState<SessionStatus | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchSession = useCallback(async () => {
    setRefreshing(true);
    setSessionError(null);
    try {
      const status = await getSessionStatus();
      setServerSession(status);
    } catch (e) {
      setSessionError(e instanceof Error ? e.message : 'Failed to fetch session');
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void fetchSession();
  }, [fetchSession]);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
          Connected Account
        </h2>
        <p className="mt-2 text-sm font-mono text-gray-900 break-all">
          {accountAddress}
        </p>
      </div>

      {/* Portal Session Status */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
            Portal Session
          </h2>
          <button
            onClick={() => void fetchSession()}
            disabled={refreshing}
            className="text-xs font-medium text-primary-600 hover:text-primary-700 disabled:opacity-50"
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {sessionError && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-3 mb-4">
            <p className="text-sm text-red-700">{sessionError}</p>
          </div>
        )}

        {serverSession && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span
                className={`inline-block w-2.5 h-2.5 rounded-full ${
                  serverSession.authenticated ? 'bg-green-500' : 'bg-red-500'
                }`}
              />
              <span className="text-sm font-medium text-gray-900">
                {serverSession.authenticated ? 'Authenticated' : 'Not authenticated'}
              </span>
            </div>

            {serverSession.authenticated && (
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <dt className="text-gray-500">Account</dt>
                  <dd className="font-mono text-gray-900 break-all text-xs mt-0.5">
                    {serverSession.account_address}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Session created</dt>
                  <dd className="text-gray-900 mt-0.5">
                    {serverSession.created_at
                      ? new Date(serverSession.created_at).toLocaleString()
                      : '\u2014'}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Last activity</dt>
                  <dd className="text-gray-900 mt-0.5">
                    {serverSession.last_activity
                      ? new Date(serverSession.last_activity).toLocaleString()
                      : '\u2014'}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Expires at</dt>
                  <dd className="text-gray-900 mt-0.5">
                    {serverSession.expires_at
                      ? new Date(serverSession.expires_at).toLocaleString()
                      : '\u2014'}
                  </dd>
                </div>
              </dl>
            )}
          </div>
        )}

        {!serverSession && !sessionError && (
          <p className="text-sm text-gray-400">Loading session info...</p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-sm font-medium text-gray-500">Consents</h3>
          <p className="mt-1 text-2xl font-semibold text-gray-900">&mdash;</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-sm font-medium text-gray-500">Payments</h3>
          <p className="mt-1 text-2xl font-semibold text-gray-900">&mdash;</p>
        </div>
      </div>
    </div>
  );
}
